import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import ApiService from "../services/api";

interface WhiteLabelSettings {
  about_link: string;
  brand_name: string;
  clinic_id: number;
  contact_link: string;
  email: string;
  id: number;
  logo: string;
  logo_url: string;
  primary_color: string;
  privacy_statement_link: string;
  subdomain: string;
  terms_of_service_link: string;
  blogs_link: string;
}

interface WhiteLabelContextType {
  whiteLabelSettings: WhiteLabelSettings | null;
  isLoading: boolean;
  error: string | null;
  brandName: string;
  logoUrl: string;
  primaryColor: string;
  contactLink: string;
  aboutLink: string;
  privacyLink: string;
  termsLink: string;
  isWhiteLabel: boolean;
  blogs_link: string;
  clinic_id?: number | null;
  isInitialized: boolean; // New flag to track initialization
}

const defaultSettings = {
  brandName: "Patient Services",
  logoUrl: "/assets/logo.svg",
  primaryColor: "#2563eb", // blue-600
  contactLink: "https://gattacagenomics.com/contact-us",
  aboutLink: "https://gattacagenomics.com/about",
  privacyLink: "https://gattacagenomics.com/privacy",
  termsLink: "https://gattacagenomics.com/terms",
  blogsLink: "https://gattacagenomics.com/blogs",
};

const WhiteLabelContext = createContext<WhiteLabelContextType | undefined>(
  undefined
);

export const useWhiteLabel = (): WhiteLabelContextType => {
  const context = useContext(WhiteLabelContext);
  if (!context) {
    throw new Error("useWhiteLabel must be used within a WhiteLabelProvider");
  }
  return context;
};

// Loading component
const WhiteLabelLoading: React.FC = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="flex flex-col items-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      <p className="mt-4 text-gray-600">Loading...</p>
    </div>
  </div>
);

interface WhiteLabelProviderProps {
  children: ReactNode;
}

export const WhiteLabelProvider: React.FC<WhiteLabelProviderProps> = ({
  children,
}) => {
  const [whiteLabelSettings, setWhiteLabelSettings] =
    useState<WhiteLabelSettings | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Extract subdomain from current URL
  const getSubdomain = (): string | null => {
    const hostname = window.location.hostname;
    const parts = hostname.split(".");

    // For localhost development, you can return a test subdomain
    if (hostname === "localhost" || hostname === "127.0.0.1") {
      // You can set a test subdomain here for development
      return "midiowa-uat"; // or return null for default behavior
    }

    // For production, extract subdomain (assuming format: subdomain.domain.com)
    if (parts.length > 2) {
      return parts[0];
    }

    return null;
  };

  const fetchWhiteLabelSettings = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const subdomain = getSubdomain();

      if (!subdomain) {
        // No subdomain found, use default settings
        setWhiteLabelSettings(null);
        setIsInitialized(true);
        setIsLoading(false);
        return;
      }

      const response = await ApiService.getClinicsWhiteLabelSettings({
        subdomain,
      });

      if (response.success && response?.data?.primary_color) {
        setWhiteLabelSettings(response.data);
      } else {
        // API returned unsuccessfully, use default settings
        setWhiteLabelSettings(null);
      }
    } catch (error) {
      console.error("Error fetching white label settings:", error);
      setError("Failed to load clinic settings");
      setWhiteLabelSettings(null);
    } finally {
      setIsLoading(false);
      setIsInitialized(true);
    }
  };

  useEffect(() => {
    fetchWhiteLabelSettings();
  }, []);

  // Apply primary color to CSS custom properties
  useEffect(() => {
    if (isInitialized) {
      if (whiteLabelSettings?.primary_color) {
        document.documentElement.style.setProperty(
          "--primary-color",
          whiteLabelSettings.primary_color
        );
      } else {
        document.documentElement.style.setProperty(
          "--primary-color",
          defaultSettings.primaryColor
        );
      }

      const title = whiteLabelSettings?.brand_name || "GG Marketplace";
      document.title = title;
    }
  }, [whiteLabelSettings, isInitialized]);

  const contextValue: WhiteLabelContextType = {
    whiteLabelSettings,
    isLoading,
    error,
    brandName: whiteLabelSettings?.brand_name || defaultSettings.brandName,
    logoUrl: whiteLabelSettings?.logo_url || defaultSettings.logoUrl,
    primaryColor:
      whiteLabelSettings?.primary_color || defaultSettings.primaryColor,
    contactLink:
      whiteLabelSettings?.contact_link || defaultSettings.contactLink,
    aboutLink: whiteLabelSettings?.about_link || defaultSettings.aboutLink,
    privacyLink:
      whiteLabelSettings?.privacy_statement_link || defaultSettings.privacyLink,
    termsLink:
      whiteLabelSettings?.terms_of_service_link || defaultSettings.termsLink,
    isWhiteLabel: whiteLabelSettings !== null,
    blogs_link: whiteLabelSettings?.blogs_link || defaultSettings.blogsLink,
    isInitialized,
    clinic_id: whiteLabelSettings?.clinic_id || null,
  };

  // Show loading screen until white label settings are resolved
  if (!isInitialized) {
    return <WhiteLabelLoading />;
  }

  return (
    <WhiteLabelContext.Provider value={contextValue}>
      {children}
    </WhiteLabelContext.Provider>
  );
};
